import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useRetailerAuth } from "../../context/RetailerAuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { SendHorizontal, Loader2, Building2, User, Phone, Mail, Smartphone, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { DEVICE_TYPES } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForwardJob() {
  const navigate = useNavigate();
  const { retailer } = useRetailerAuth();
  const [loading, setLoading] = useState(false);
  const [partnerShops, setPartnerShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [createdJob, setCreatedJob] = useState(null);

  const [formData, setFormData] = useState({
    tenant_id: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    device_type: "",
    device_brand: "",
    device_model: "",
    problem_description: "",
    estimated_cost: "",
    retailer_notes: "",
  });

  useEffect(() => {
    fetchPartnerShops();
  }, []);

  const fetchPartnerShops = async () => {
    try {
      const response = await axios.get(`${API}/retailer/partner-shops`);
      setPartnerShops(response.data.shops);
    } catch (error) {
      console.error("Failed to fetch partner shops:", error);
      toast.error("Failed to load partner shops");
    } finally {
      setShopsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tenant_id) {
      toast.error("Please select a repair shop");
      return;
    }
    if (!formData.customer_name || !formData.customer_phone) {
      toast.error("Customer name and phone are required");
      return;
    }
    if (!formData.device_type || !formData.device_brand || !formData.device_model) {
      toast.error("Device details are required");
      return;
    }
    if (!formData.problem_description) {
      toast.error("Please describe the problem");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
      };
      const response = await axios.post(`${API}/retailer/forward-job`, payload);
      setCreatedJob(response.data.job);
      setSuccess(true);
      toast.success("Job forwarded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to forward job");
    } finally {
      setLoading(false);
    }
  };

  if (success && createdJob) {
    return (
      <div className="p-6">
        <div className="max-w-lg mx-auto">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Job Forwarded!</h2>
              <p className="text-gray-600 mb-6">
                The repair job has been sent to {createdJob.repair_shop}
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500">Job Number</p>
                <p className="text-xl font-bold text-orange-600">{createdJob.job_number}</p>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setSuccess(false);
                    setCreatedJob(null);
                    setFormData({
                      tenant_id: formData.tenant_id,
                      customer_name: "",
                      customer_phone: "",
                      customer_email: "",
                      device_type: "",
                      device_brand: "",
                      device_model: "",
                      problem_description: "",
                      estimated_cost: "",
                      retailer_notes: "",
                    });
                  }}
                >
                  Forward Another
                </Button>
                <Button 
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  onClick={() => navigate("/retailer/jobs")}
                >
                  View All Jobs
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/retailer/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Forward New Job</h1>
            <p className="text-gray-600">Send a repair job to your partner repair shop</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Select Repair Shop */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-600" />
                Select Repair Shop
              </CardTitle>
              <CardDescription>Choose which partner shop to forward this job to</CardDescription>
            </CardHeader>
            <CardContent>
              {shopsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
                </div>
              ) : partnerShops.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-3">No partner shops added yet</p>
                  <Button variant="outline" onClick={() => navigate("/retailer/partners")}>
                    Add Partner Shop
                  </Button>
                </div>
              ) : (
                <Select value={formData.tenant_id} onValueChange={(v) => setFormData({ ...formData, tenant_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a repair shop..." />
                  </SelectTrigger>
                  <SelectContent>
                    {partnerShops.map((shop) => (
                      <SelectItem key={shop.id} value={shop.id}>
                        {shop.name} ({shop.subdomain}.aftersales.pro)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </CardContent>
          </Card>

          {/* Customer Details */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Customer Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customer_name">Customer Name *</Label>
                  <Input
                    id="customer_name"
                    placeholder="John Doe"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer_phone">Phone Number *</Label>
                  <Input
                    id="customer_phone"
                    placeholder="9876543210"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_email">Email (Optional)</Label>
                <Input
                  id="customer_email"
                  type="email"
                  placeholder="customer@email.com"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Device Details */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-green-600" />
                Device Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Device Type *</Label>
                  <Select value={formData.device_type} onValueChange={(v) => setFormData({ ...formData, device_type: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DEVICE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="device_brand">Brand *</Label>
                  <Input
                    id="device_brand"
                    placeholder="Apple, Samsung..."
                    value={formData.device_brand}
                    onChange={(e) => setFormData({ ...formData, device_brand: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="device_model">Model *</Label>
                  <Input
                    id="device_model"
                    placeholder="iPhone 15, Galaxy S24..."
                    value={formData.device_model}
                    onChange={(e) => setFormData({ ...formData, device_model: e.target.value })}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Problem Details */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Problem Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="problem">Problem Description *</Label>
                <Textarea
                  id="problem"
                  placeholder="Describe the issue reported by customer..."
                  value={formData.problem_description}
                  onChange={(e) => setFormData({ ...formData, problem_description: e.target.value })}
                  rows={4}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="estimated_cost">Estimated Cost (Optional)</Label>
                  <Input
                    id="estimated_cost"
                    type="number"
                    placeholder="0"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes for Repair Shop</Label>
                  <Input
                    id="notes"
                    placeholder="Any special instructions..."
                    value={formData.retailer_notes}
                    onChange={(e) => setFormData({ ...formData, retailer_notes: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate("/retailer/dashboard")}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={loading || partnerShops.length === 0}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <SendHorizontal className="w-4 h-4 mr-2" />}
              Forward Job
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
